import React from 'react';
import { Box, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { isValidGtin } from 'src/Lca/gtin';

// GS1 human readable interpretation of the GTIN printed next to the data carrier:
// application identifier 01 in parentheses, followed by the GTIN padded to 14 digits.
// Codes reaching this point through the GTIN import or an Excel upload are not
// necessarily well formed, and printing "(01)" in front of a malformed code would
// present it as a GS1 identifier it is not - so anything invalid renders nothing.
export const getGtinHri = (gtinCode?: string | null) => {
  const gtin = (gtinCode || '').trim();
  if (!isValidGtin(gtin)) return null;
  return `(01) ${gtin.padStart(14, '0')}`;
};

interface PassportQrCodeProps {
  passportUrl: string;
  size: number;
  imageUrl?: string | null;
  gtinCode?: string | null;
}

export default function PassportQrCode({
  passportUrl,
  size,
  imageUrl,
  gtinCode,
}: PassportQrCodeProps) {
  const { t } = useTranslation('lca');
  const hri = getGtinHri(gtinCode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={t('product.sharePage.customQrAlt')}
          sx={{ width: size, height: size, objectFit: 'contain' }}
        />
      ) : (
        <QRCodeCanvas
          value={passportUrl}
          size={size}
          includeMargin
          bgColor="#FFFFFF"
          fgColor="#000000"
          title={t('product.sharePage.qrCodeAlt')}
        />
      )}

      {hri && (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            letterSpacing: '0.04em',
            textAlign: 'center',
          }}
        >
          {hri}
        </Typography>
      )}
    </Box>
  );
}
